import React from "react";
import Header from "components/Header";

import {
    Chart as ChartJS,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";

import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
    FaExclamationTriangle,
    FaChartLine,
    FaSyringe,
    FaCapsules,
    FaHeartbeat,
} from "react-icons/fa";
const stateAlerts = [
    {
        title: "High MMR / IMR Districts",
        description: "14 districts reporting MMR / IMR above state thresholds",
        severity: "red",
        icon: FaExclamationTriangle,
    },
    {
        title: "Rapid Rise in Dengue / TB / Malaria",
        description: "Sharp month-on-month increase reported in 9 districts",
        severity: "red",
        icon: FaChartLine,
    },
    {
        title: "Low Immunization Coverage",
        description: "Coverage below 80% in 11 districts",
        severity: "amber",
        icon: FaSyringe,
    },
    {
        title: "Medicine Stock-outs",
        description: "23 facilities reported zero stock of key essential medicines",
        severity: "red",
        icon: FaCapsules,
    },
    {
        title: "Rising Hypertension / Diabetes Trends",
        description: "Consistent upward trend observed across multiple districts",
        severity: "amber",
        icon: FaHeartbeat,
    },
];
const diseaseTrendData = {
    labels: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],

    // Dengue peaks during monsoon (Jul–Oct)
    dengue: [
        4200, 4600, 5100, 5800, 7200, 9800,
        15600, 21400, 24800, 18200, 9600, 5200
    ],

    // TB is relatively stable with gradual increase
    tb: [
        28600, 29100, 29800, 30400, 30900, 31500,
        31800, 32100, 32500, 39900, 33300, 33800
    ],

    // Malaria rises with rains, drops in winter
    malaria: [
        3100, 3400, 4200, 5200, 7600, 11200,
        18400, 21600, 19800, 14200, 6800, 3900
    ],
};

const DiseaseSurgeTrendChart = () => (
    <div className="bg-white rounded-xl shadow-sm p-3">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Rapid Rise in Communicable Diseases
        </h3>

        <div className="">
            <Line
                data={{
                    labels: diseaseTrendData.labels,
                    datasets: [
                        {
                            label: "Dengue",
                            data: diseaseTrendData.dengue,
                            borderColor: "#f43f5e",
                            tension: 0.4,
                        },
                        {
                            label: "TB",
                            data: diseaseTrendData.tb,
                            borderColor: "#6366f1",
                            tension: 0.4,
                        },
                        {
                            label: "Malaria",
                            data: diseaseTrendData.malaria,
                            borderColor: "#22c55e",
                            tension: 0.4,
                        },
                    ],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        datalabels: false,
                        legend: { position: "bottom" },
                        tooltip: { mode: "index", intersect: false },
                    },
                    scales: {
                        y: { grid: { color: "#f1f5f9" } },
                        x: { grid: { display: false } },
                    },
                }}
            />
        </div>
    </div>
);
const immunizationTrend = {
    labels: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],
    coverage: [
        848, 823, 582, 880, 708, 707,   // summer dip
        782, 795, 891, 892, 893, 840    // recovery & stabilization
    ],
    target: 980
};


const ResponseProgress = ({ label, value }) => (
    <div>
        <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-700">{label}</span>
            <span className="text-gray-600">{value}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded">
            <div
                className="h-2 rounded bg-indigo-500"
                style={{ width: `${value}%` }}
            />
        </div>
    </div>
);
const responseMetrics = [
    { label: "Districts with Action Plans Submitted", value: 72 },
    { label: "Districts Reviewed in Last 30 Days", value: 64 },
    { label: "Stock-outs Resolved", value: 81 },
];

const ImmunizationCoverageTrend = () => (
    <div className="bg-white rounded-xl shadow-sm p-3">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Immunization Coverage Trend (%)
        </h3>

        <div>
            <Line
                data={{
                    labels: immunizationTrend.labels,
                    datasets: [
                        {
                            label: "Coverage",
                            data: immunizationTrend.coverage,
                            borderColor: "#f97316",
                            backgroundColor: "rgba(249,115,22,0.15)",
                            fill: true,
                            tension: 0.4,
                        },
                        {
                            label: "Target",
                            data: immunizationTrend.labels.map(
                                () => immunizationTrend.target
                            ),
                            borderColor: "#94a3b8",
                            borderDash: [4, 4],
                            pointRadius: 0,
                        },
                    ],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: true, // 🔑 important
                    plugins: {
                        legend: { position: "bottom" },
                    },
                    scales: {
                        y: {
                            max: 1000,
                            ticks: { callback: (v) => `${v}%` },
                        },
                        x: { grid: { display: false } },
                    },
                }}
            />
        </div>
    </div>
);

const stockOutTrend = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    facilities: [8, 11, 14, 17, 20, 23],
};
const StockOutTrendChart = () => (
    <div className="bg-white rounded-xl shadow-sm p-3">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Facilities Reporting Medicine Stock-outs
        </h3>

        <div className="">
            <Bar
                data={{
                    labels: stockOutTrend.labels,
                    datasets: [
                        {
                            label: "Facilities",
                            data: stockOutTrend.facilities,
                            backgroundColor: "#ef4444",
                            borderRadius: 6,
                            barThickness: 28,
                        },
                    ],
                }}
                options={{
                    plugins: {
                        legend: { display: false },
                        datalabels: false,
                    },
                    scales: {
                        y: { grid: { color: "#f1f5f9" } },
                        x: { grid: { display: false } },
                    },
                }}
            />
        </div>
    </div>
);

const AlertRow = ({ title, description, severity, icon: Icon }) => {
    const severityStyles = {
        red: {
            border: "border-l-4 border-rose-500",
            bg: "bg-rose-50",
            text: "text-rose-800",
        },
        amber: {
            border: "border-l-4 border-amber-500",
            bg: "bg-amber-50",
            text: "text-amber-800",
        },
    };

    const s = severityStyles[severity];

    return (
        <div
            className={`bg-white rounded-lg shadow-sm p-3 flex gap-3 ${s.border}`}
        >
            {/* Icon */}
            <div
                className={`h-10 w-10 flex items-center justify-center rounded-md ${s.bg} ${s.text}`}
            >
                <Icon size={18} />
            </div>

            {/* Content */}
            <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                    {title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                    {description}
                </p>
            </div>
        </div>
    );
};

export default function AlertsEarlyWarningIndicatorsDashboard() {
    const handleMenuClick = () => {
        console.log("menu clicked (header)");
    };

    return (
        <>
            <Header onMenuClick={handleMenuClick} dashboardName="Alerts & Early Warning Indicators Dashboard" />
            <div className="p-3">
                <section className="space-y-4">
                    <div className="grid grid-cols-12 gap-6">
                        {/* Disease Surge Trend */}
                        <div className="col-span-12 lg:col-span-5">
                        <StockOutTrendChart />
                           
                        </div>
                        {/* Immunization Coverage */}
                        <div className="col-span-12 lg:col-span-4">
                        <DiseaseSurgeTrendChart />
                           
                        </div>
                        {/* Alerts – col-4 */}
                        <div className="col-span-12 lg:col-span-3">
                            <div className="space-y-3">
                                {stateAlerts.map((alert, idx) => (
                                    <AlertRow key={idx} {...alert} />
                                ))}
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-5">
                        <ImmunizationCoverageTrend />
                        </div>
                        <div className="col-span-12 lg:col-span-4">
                       

                            <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Response Readiness & Action Tracking
                            </h2>
                                {responseMetrics.map((m, i) => (
                                    <ResponseProgress key={i} {...m} />
                                ))}
                            </div>
                        </div>
                    </div>




                </section >

            </div >
        </>
    );
}